import { useEffect, useMemo } from 'react';
import { useGetFileDescriptions } from '../create-new-task/use-get-file-descriptions';
import { CrmName } from 'components/aufgaben-component/task.prop';
import { FormFieldTypes } from 'common/enums';
import { useEmployeesQuery } from '../create-new-task/use-employees-query';
import { useVehicleForCreateTaskQuery } from 'hooks/react-query/tasks/vehicle-for-create-task';
import { UseEditTaskProps } from './edit-task.types';
import moment from 'moment';
import { FileProps } from 'pages/vehicle-return-page/vehicle-return-page.props';
import { getRequestFormData } from 'utils/get-response-data';
import { allFieldsCreateTask } from '../create-new-task/formFields';
import { AnyObject } from 'chart.js/dist/types/basic';
import { useOverlay } from 'hooks';
import { editTask } from 'components/aufgaben-component/utils';
import { useDispatch } from 'react-redux';
import { setSnakbar } from 'actions/app_action';
import { SaveTo } from '../create-new-task/use-create-new-task-modal';
import { errorMessages } from 'utils/error-messages';

export const convertFilesForField = (files: FileProps[]) => {
  return files.map(file => {
    return {
      path: file.path,
      lastModified: moment(file.modifiedOn).valueOf(),
      lastModifiedDate: moment(file.modifiedOn).toISOString(),
      name: file.fileName,
      size: Number(file.size),
      id: file.fileId
    };
  });
};

export const useEditTask = ({
  isUploadDocumentTask,
  reactHookFormData,
  taskData,
  documentType,
  saveDocumentTo,
  refresh,
  onCancel
}: UseEditTaskProps) => {
  const {
    handleSubmit,
    formState: { dirtyFields },
    watch,
    setValue,
    clearErrors
  } = reactHookFormData;

  const currentDueDateOption = watch('dateOptions');
  const isCalendarVisible = currentDueDateOption === 'choose';
  const currentDueDateCalendar = watch('dateChosen');
  const currentVehicle = watch(CrmName.Vehicle);

  const [showOverlay, hideOverlay] = useOverlay();
  const dispatch = useDispatch();
  const { fileDescriptions, isFileDescriptionsLoading } = useGetFileDescriptions();
  const { employeesOptions, isEmployeesLoading } = useEmployeesQuery(false, !isUploadDocumentTask);

  const { vehicleForCreateTaskData, isVehicleForCreateTaskFetching } = useVehicleForCreateTaskQuery(
    undefined,
    !isUploadDocumentTask
  );

  const employeesFormField = useMemo(() => {
    return [
      {
        name: CrmName.Employee,
        type: FormFieldTypes.Lookup,
        defaultValue: '',
        label: 'Mitarbeiter',
        options: employeesOptions,
        validation: {
          required: 'Bitte auswählen'
        }
      }
    ];
  }, [employeesOptions]);

  const vehicleFormField = useMemo(() => {
    return [
      {
        name: CrmName.Vehicle,
        type: FormFieldTypes.Lookup,
        defaultValue: '',
        label: 'Fahrzeug',
        options: vehicleForCreateTaskData?.map(dataItem => ({
          value: dataItem.new_fuhrparkid.attributeValue || '',
          label: dataItem.new_name.attributeValue || ''
        })),
        validation: {}
      }
    ];
  }, [vehicleForCreateTaskData]);

  const filesDescriptionField = useMemo(() => {
    return [
      {
        name: CrmName.DocumentType,
        type: FormFieldTypes.OptionSet,
        defaultValue: documentType,
        label: 'Dokumenttyp auswählen',
        options: fileDescriptions,
        validation: {
          required: 'Bitte auswählen'
        }
      }
    ];
  }, [fileDescriptions]);

  const dueDateField = useMemo(() => {
    return [
      {
        name: CrmName.DateOptions,
        type: FormFieldTypes.OptionSet,
        defaultValue: '',
        label: 'Fälligkeit',
        options: [
          {
            label: 'Heute',
            value: 'today'
          },
          {
            label: 'In 2 Tagen',
            value: 2
          },
          {
            label: 'In 1 Woche',
            value: 7
          },
          {
            label: 'In 2 Wochen',
            value: 14
          },
          {
            label: 'In 1 Monat',
            value: 30
          },
          {
            label: 'Auswählen',
            value: 'choose'
          }
        ],
        validation: {
          required: 'Bitte auswählen'
        }
      }
    ];
  }, []);

  const dueDateCalendarField = useMemo(() => {
    return [
      {
        name: CrmName.DateChosen,
        type: FormFieldTypes.Date,
        defaultValue: null,
        validation: {
          required: 'Bitte auswählen'
        },
        minDate: moment().startOf('day')
      }
    ];
  }, []);

  const titleField = useMemo(() => {
    return [
      {
        name: CrmName.Title,
        type: FormFieldTypes.Text,
        defaultValue: '',
        label: 'Titel',
        validation: {
          required: 'Bitte auswählen',
          maxLength: {
            value: 200,
            message: errorMessages.getMaxTextLength(200)
          }
        }
      }
    ];
  }, []);

  const descriptionField = useMemo(() => {
    return [
      {
        name: CrmName.Description,
        type: FormFieldTypes.TextArea,
        label: 'Beschreibung',
        defaultValue: '',
        validation: {}
      }
    ];
  }, []);

  const filesField = useMemo(() => {
    return [
      {
        name: CrmName.Files,
        type: FormFieldTypes.File,
        defaultValue: [],
        validation: {},
        dropzoneOptions: {
          maxFiles: 6,
          multiple: true,
          maxSize: 1024 * 1024 * 10
        }
      }
    ];
  }, []);

  const saveDocumentToFiled = useMemo(() => {
    return [
      {
        name: CrmName.SaveDocumentTo,
        type: FormFieldTypes.OptionSet,
        label: 'Die hinzugefügte Datei speichern',
        defaultValue: saveDocumentTo,
        options: [
          { label: 'Fahrzeug', value: SaveTo.Car },
          { label: 'Fahrer', value: SaveTo.Driver }
        ],
        disabled: true,
        validation: {}
      }
    ];
  }, [saveDocumentTo]);

  const findDriverForVehicle = (vehicleId: string) => {
    const findDataByVehicle = vehicleForCreateTaskData?.find(
      dataItem => dataItem.new_fuhrparkid.attributeValue === vehicleId
    );

    const isDriverInEmployees = employeesOptions.some(
      employee => employee.value === findDataByVehicle?.new_fahrerid?.attributeValue?.id
    );

    if (isDriverInEmployees) {
      return findDataByVehicle?.new_fahrerid?.attributeValue?.id;
    }
  };

  const onSubmit = () => {
    handleSubmit(async data => {
      showOverlay();
      const newTaskData: AnyObject = getRequestFormData(data, allFieldsCreateTask);
      if (data[CrmName.Vehicle]) {
        newTaskData.regardingobjectid.attributeValue.logicalName = 'new_fuhrpark';
      }

      newTaskData.uds_executor_contactid = {
        attributeValue: { logicalName: 'contact' }
      };

      const formData = new FormData();
      const existingFilesIds = data.files
        ? data.files.filter((file: AnyObject) => file.id).map((file: AnyObject) => file.id)
        : [];

      formData.append('attributes', JSON.stringify(newTaskData));
      formData.append('id', taskData.taskId.attributeValue!);
      formData.append('existingFiles', JSON.stringify(existingFilesIds));

      if (data.files) {
        data.files
          .filter((file: AnyObject) => !file.id)
          .forEach((file: File) => {
            formData.append('newfiles', file);
          });
      }

      editTask(formData)
        .then(response => {
          refresh();
          onCancel();
          dispatch(
            setSnakbar({
              isOpen: true,
              message: response.data.isSuccess
                ? 'Die Aufgabe wurde erfolgreich aktualisiert.'
                : 'Fehler, die Aufgabe wurde nicht geändert.',
              type: response.data.level.toLowerCase()
            })
          );
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          hideOverlay();
        });
    })();
  };

  useEffect(() => {
    if (currentVehicle && dirtyFields[CrmName.Vehicle]) {
      const newDriver = findDriverForVehicle(currentVehicle);
      setValue(CrmName.Employee, newDriver);
      if (newDriver) {
        clearErrors(CrmName.Employee);
      }
    }
  }, [clearErrors, currentVehicle, dirtyFields, findDriverForVehicle, setValue]);

  useEffect(() => {
    if (isCalendarVisible) {
      setValue(CrmName.DueDate, moment.utc(currentDueDateCalendar).format('YYYY-MM-DD'));
    } else {
      if (currentDueDateOption === 'today') {
        setValue(CrmName.DueDate, moment().format('YYYY-MM-DD'));
      } else {
        setValue(CrmName.DueDate, moment().add(currentDueDateOption, 'days').format('YYYY-MM-DD'));
      }
    }
  }, [currentDueDateCalendar, currentDueDateOption, isCalendarVisible, setValue]);

  useEffect(() => {
    if (!isUploadDocumentTask) {
      setValue(CrmName.Vehicle, taskData.vehicleId.attributeValue?.id, { shouldDirty: false });
      setValue(CrmName.Employee, taskData.executorId.attributeValue?.id, { shouldDirty: false });
      setValue(CrmName.Files, convertFilesForField(taskData.files), { shouldDirty: false });
    }

    setValue(CrmName.Title, taskData.title.attributeValue, { shouldDirty: false });
    setValue(CrmName.Description, taskData.description.attributeValue, { shouldDirty: false });
    setValue(CrmName.DateOptions, 'choose');
    setValue(CrmName.DateChosen, moment(taskData.dueDate.attributeValue), { shouldDirty: false });

    setValue(CrmName.DocumentType, documentType, { shouldDirty: false });
    setValue(CrmName.SaveDocumentTo, saveDocumentTo, { shouldDirty: false });
  }, []);

  return {
    titleField,
    employeesFormField,
    vehicleFormField,
    filesDescriptionField,
    dueDateField,
    dueDateCalendarField,
    descriptionField,
    filesField,
    saveDocumentToFiled,
    isCalendarVisible,
    isLoading: isEmployeesLoading || isFileDescriptionsLoading || isVehicleForCreateTaskFetching,
    onSubmit
  };
};
